<!-- Общий выбор количества: числовой ввод и ползунок в пределах доступного остатка. -->
<template>
  <div class="quantity-slider">
    <base-input
      :id="inputId"
      :model-value="modelValue"
      type="number"
      :invalid="invalid"
      @update:model-value="$emit('update:model-value', $event)"
    />
    <div class="quantity-slider__scale" :class="{ 'quantity-slider__scale--disabled': available <= 0 }">
      <div class="quantity-slider__track" :style="trackStyle" aria-hidden="true" />
      <input
        class="quantity-slider__slider"
        type="range"
        min="0"
        :max="available"
        :step="Number.isInteger(available) ? 1 : 'any'"
        :value="sliderValue"
        :disabled="available <= 0"
        :aria-label="ariaLabel"
        :aria-valuetext="`${sliderValue} ${unit}${isAll ? ' — всё' : ''}`"
        @input="$emit('update:model-value', Number($event.target.value))"
      />
      <div class="quantity-slider__marks" aria-hidden="true">
        <span
          v-for="(mark, index) in marks"
          :key="mark"
          class="quantity-slider__mark"
          :class="{
            'quantity-slider__mark--passed': mark <= sliderValue,
            'quantity-slider__mark--first': index === 0,
            'quantity-slider__mark--last': index === marks.length - 1,
          }"
          :style="{ left: `${available > 0 ? mark / available * 100 : 0}%` }"
        >
          <span class="quantity-slider__tick" />
          <span class="quantity-slider__mark-label">{{ formatMark(mark) }}</span>
        </span>
      </div>
    </div>
    <div class="quantity-slider__limits">
      <span>{{ isAll ? "Весь остаток" : "Выберите количество" }}</span>
      <span :class="{ 'quantity-slider__all': isAll }">
        {{ isAll ? allLabel : 'Всё' }}: {{ available }} {{ unit }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "BaseQuantitySlider",
  props: {
    modelValue: { type: [String, Number], default: "1" },
    available: { type: Number, required: true },
    unit: { type: String, default: "" },
    inputId: { type: String, default: "stock-quantity" },
    ariaLabel: { type: String, default: "Количество" },
    allLabel: { type: String, default: "Всё" },
    invalid: Boolean,
  },
  emits: ["update:model-value"],
  computed: {
    sliderValue() {
      const value = Number(this.modelValue);
      return Number.isFinite(value) ? Math.min(this.available, Math.max(0, value)) : 0;
    },
    trackStyle() {
      const progress = this.available > 0 ? this.sliderValue / this.available * 100 : 0;
      return { background: `linear-gradient(to right, var(--color-green) ${progress}%, var(--color-grey) ${progress}%)` };
    },
    marks() {
      if (this.available <= 0) return [0];
      if (Number.isInteger(this.available) && this.available <= 10) {
        return Array.from({ length: this.available + 1 }, (_, index) => index);
      }
      // Крупные остатки делим на читаемые интервалы 1/2/5 × 10ⁿ.
      const roughStep = this.available / 5;
      const magnitude = 10 ** Math.floor(Math.log10(roughStep));
      const multiplier = [1, 2, 5, 10].find((value) => value * magnitude >= roughStep);
      const step = multiplier * magnitude;
      const marks = [0];
      for (let index = 1; index * step < this.available; index += 1) {
        const value = Number((index * step).toPrecision(12));
        // Не ставим две подписи вплотную перед конечной отметкой.
        if (this.available - value >= step * 0.45) marks.push(value);
      }
      return [...marks, this.available];
    },
    isAll() {
      return this.available > 0 && Number(this.modelValue) === this.available;
    },
  },
  methods: {
    formatMark(value) {
      return new Intl.NumberFormat("ru-RU", {
        notation: value >= 10000 ? "compact" : "standard",
        maximumSignificantDigits: 4,
      }).format(value);
    },
  },
};
</script>

<style scoped>
.quantity-slider {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.quantity-slider__scale {
  position: relative;
  height: 56px;
}
.quantity-slider__track {
  position: absolute;
  top: 19px;
  left: 10px;
  right: 10px;
  height: 6px;
  border-radius: 6px;
}
.quantity-slider__slider {
  position: relative;
  z-index: 1;
  appearance: none;
  -webkit-appearance: none;
  display: block;
  width: 100%;
  height: 44px;
  margin: 0;
  background: transparent;
  cursor: pointer;
}
.quantity-slider__slider::-webkit-slider-runnable-track {
  height: 6px;
  border: 0;
  background: transparent;
}
.quantity-slider__slider::-moz-range-track {
  height: 6px;
  border: 0;
  background: transparent;
}
.quantity-slider__slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  margin-top: -7px;
  border: 3px solid var(--color-green);
  border-radius: 50%;
  background: var(--color-light);
  box-shadow: 0 2px 5px rgb(0 0 0 / 12%);
  transition: border-color .15s, box-shadow .15s;
}
.quantity-slider__slider::-moz-range-thumb {
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border: 3px solid var(--color-green);
  border-radius: 50%;
  background: var(--color-light);
  box-shadow: 0 2px 5px rgb(0 0 0 / 12%);
}
.quantity-slider__slider:hover::-webkit-slider-thumb {
  border-color: var(--color-green-hover);
  box-shadow: 0 0 0 4px rgb(0 191 165 / 12%);
}
.quantity-slider__slider:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
  border-radius: 6px;
}
.quantity-slider__scale--disabled { opacity: .5; }
.quantity-slider__slider:disabled { cursor: default; }
.quantity-slider__marks {
  position: absolute;
  inset: 0 10px;
  pointer-events: none;
}
.quantity-slider__mark {
  position: absolute;
  top: 29px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  transform: translateX(-50%);
  color: var(--color-dark);
  font-size: 11px;
  line-height: 1;
}
.quantity-slider__tick { width: 1px; height: 5px; background: var(--color-grey); }
.quantity-slider__mark--passed .quantity-slider__tick { background: var(--color-green); }
.quantity-slider__mark--first .quantity-slider__mark-label { align-self: flex-start; }
.quantity-slider__mark--last .quantity-slider__mark-label { align-self: flex-end; }
.quantity-slider__limits {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  line-height: 1.4;
}
.quantity-slider__all { color: var(--color-green-hover); font-weight: 600; }
</style>
